/**
 * Purpose:
 *   Editorial-page behaviours for Viva Voce. Powers the Shipping & Returns,
 *   Size Guide and FAQ templates: an auto-built sticky tab nav with scroll-spy,
 *   FAQ live-search filtering, single-open <details> enforcement across the
 *   page, and an IN/CM size-chart unit toggle. No external dependencies; every
 *   behaviour is a native custom element. The whole file is guarded by
 *   window.__vvEditorialLoaded so registration runs at most once even when
 *   several sections enqueue the script on one page.
 *
 * Public API:
 *   Registers three custom elements (no exported symbols):
 *     - <vv-editorial-tabs>  (VVEditorialTabs): scans for [data-vv-tab-label]
 *       sections, renders one tab link per section, scroll-spy + smooth-scroll,
 *       and moves keyboard focus into the target section on tab activation.
 *     - <vv-faq-search>      (VVFaqSearch): filters .vv-faq__item by text;
 *       collapses empty [data-vv-faq-group] groups and toggles
 *       [data-vv-faq-empty] notes.
 *     - <vv-size-toggle>     (VVSizeToggle): swaps [data-in]/[data-cm] cell
 *       values and the active [data-vv-unit] button state.
 *   Also installs one capture-phase document `toggle` listener enforcing a
 *   single open details.vv-faq__item at a time.
 *
 * Upstream dependencies:
 *   - DOM only (HTMLElement, customElements, window scroll/resize). No external
 *     libraries.
 *   - Reads #header-group to measure the live sticky-header height for the tab
 *     nav offset (--vv-ed-header-h); falls back to the wrapper height.
 *
 * Downstream consumers:
 *   - Enqueued via <script src="vv-editorial.js"> by sections/vv-article-section,
 *     vv-careers-roles, vv-editorial-hero, vv-editorial-tabs, vv-faq-section,
 *     and vv-size-chart.
 *   - The custom elements are rendered by sections/vv-editorial-tabs,
 *     vv-editorial-hero, and vv-size-chart, used on templates/page.faq.json,
 *     page.shipping-returns.json and page.size-guide.json.
 *
 * Failure modes:
 *   - No throws. Every entry point guards on the elements it needs: missing
 *     #header-group -> offset 0; missing tabs list or zero tab-labelled
 *     sections -> the tab nav hides itself; missing FAQ input/form or size
 *     table -> the handler returns early. Re-running registration is a no-op
 *     (customElements.get guard + the __vvEditorialLoaded flag).
 *
 * Performance:
 *   - Scroll and resize listeners are passive. Scroll-spy work is a single
 *     bounding-rect pass over the labelled sections per scroll event; no
 *     layout thrash beyond the reads. FAQ search filters on input over the
 *     current DOM node list. No timers, no network.
 */
if (!window.__vvEditorialLoaded) {
  window.__vvEditorialLoaded = true;

/* Keep the sticky tab nav offset in sync with the live header height so the
   tabs park directly under the header on every breakpoint.

   Horizon's #header-group wrapper is `display:contents` (0 box height) and the
   announcement bar inside it is non-sticky (scrolls away), while the <header>
   itself is `position:sticky` and pins to the top. So the offset the tabs need
   is the height of whatever actually stays pinned — the sticky header — not the
   collapsed wrapper. Find the first sticky/fixed element in the header group and
   measure that; fall back to the wrapper's own height. */
function vvHeaderOffset() {
  const group = document.getElementById('header-group');
  if (!group) return 0;
  const pinned = Array.prototype.find.call(group.querySelectorAll('*'), (el) => {
    const pos = getComputedStyle(el).position;
    return pos === 'sticky' || pos === 'fixed';
  });
  return pinned ? pinned.offsetHeight : group.offsetHeight;
}

function vvSyncHeaderHeight() {
  document.documentElement.style.setProperty('--vv-ed-header-h', vvHeaderOffset() + 'px');
}
window.addEventListener('resize', vvSyncHeaderHeight, { passive: true });

function vvScrollOffset(tabNav) {
  const headerH = vvHeaderOffset();
  const navH = tabNav ? tabNav.offsetHeight : 0;
  return headerH + navH + 8;
}

/* ── Auto-building sticky tab nav with scroll-spy ──────────────
   Scans the page for content sections that declare a tab label and
   renders one tab link per section. Anchors derive from each section's
   id, so the merchant never has to keep ids in sync by hand. */
class VVEditorialTabs extends HTMLElement {
  connectedCallback() {
    this.listEl = this.querySelector('[data-vv-tabs-list]');
    this.onScroll = this.onScroll.bind(this);
    this._scrollBound = false;
    // Build once the sibling sections are guaranteed to be in the DOM. If the
    // document is still parsing, wait for DOMContentLoaded (fires after all
    // sections are parsed, before images). Otherwise the element upgraded after
    // parsing, so the sections already exist — build synchronously. We avoid
    // requestAnimationFrame here because it is unreliable in headless/background
    // contexts where the page never paints. build() is idempotent.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.build(), { once: true });
    } else {
      this.build();
    }
  }

  build() {
    this.sections = Array.from(document.querySelectorAll('[data-vv-tab-label]'));
    if (!this.listEl || this.sections.length === 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    this.listEl.innerHTML = '';
    this.tabs = this.sections.map((section, index) => {
      const label = section.getAttribute('data-vv-tab-label');
      const id = section.id;
      const tab = document.createElement('a');
      tab.className = 'vv-ed-tabs__link';
      tab.href = '#' + id;
      tab.textContent = label;
      tab.dataset.target = id;
      if (index === 0) tab.classList.add('is-active');
      tab.addEventListener('click', (event) => this.onTabClick(event, section));
      this.listEl.appendChild(tab);
      return tab;
    });

    vvSyncHeaderHeight();
    if (!this._scrollBound) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this._scrollBound = true;
    }
    this.onScroll();
  }

  onTabClick(event, section) {
    event.preventDefault();
    const top =
      section.getBoundingClientRect().top + window.pageYOffset - vvScrollOffset(this);
    window.scrollTo({ top, behavior: 'smooth' });
    // Advance keyboard focus into the target content so the user is not trapped
    // in the position:sticky tab bar. tabindex="-1" makes the section
    // programmatically focusable without adding it to the tab order;
    // preventScroll keeps the focus call from fighting the smooth scroll above.
    if (!section.hasAttribute('tabindex')) section.setAttribute('tabindex', '-1');
    section.focus({ preventScroll: true });
  }

  onScroll() {
    const offset = vvScrollOffset(this) + 24;
    let current = null;
    this.sections.forEach((section) => {
      if (section.getBoundingClientRect().top <= offset) current = section.id;
    });
    this.tabs.forEach((tab) => {
      tab.classList.toggle('is-active', tab.dataset.target === current);
    });
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }
}
if (!customElements.get('vv-editorial-tabs')) customElements.define('vv-editorial-tabs', VVEditorialTabs);

/* ── FAQ live search ───────────────────────────────────────────
   Filters every FAQ item on the page by its question + answer text.
   Empty groups collapse and a "no results" note appears when nothing
   matches. */
class VVFaqSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.form = this.querySelector('form');
    if (this.form) this.form.addEventListener('submit', (e) => e.preventDefault());
    if (!this.input) return;
    this.input.addEventListener('input', () => this.filter());
  }

  filter() {
    const query = this.input.value.trim().toLowerCase();
    const items = document.querySelectorAll('.vv-faq__item');
    const groups = document.querySelectorAll('[data-vv-faq-group]');
    let totalMatches = 0;

    items.forEach((item) => {
      const text = (item.textContent || '').toLowerCase();
      const match = query === '' || text.indexOf(query) !== -1;
      item.hidden = !match;
      if (match) totalMatches += 1;
    });

    // Hide a whole group (heading included) when none of its items match.
    groups.forEach((group) => {
      const visible = group.querySelectorAll('.vv-faq__item:not([hidden])').length;
      group.hidden = query !== '' && visible === 0;
    });

    document.querySelectorAll('[data-vv-faq-empty]').forEach((note) => {
      note.hidden = !(query !== '' && totalMatches === 0);
    });
  }
}
if (!customElements.get('vv-faq-search')) customElements.define('vv-faq-search', VVFaqSearch);

/* Enforce a single open FAQ item across the whole page, matching the
   reference behaviour. Uses native <details> for accessibility. */
document.addEventListener('toggle', (event) => {
  const el = event.target;
  if (!(el instanceof HTMLDetailsElement) || !el.classList.contains('vv-faq__item')) return;
  if (!el.open) return;
  document.querySelectorAll('details.vv-faq__item[open]').forEach((other) => {
    if (other !== el) other.open = false;
  });
}, true);

/* ── Size chart unit toggle (IN / CM) ──────────────────────────
   Each measurement cell carries data-in / data-cm; the toggle swaps the
   rendered value and the active button state. */
class VVSizeToggle extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-vv-unit]'));
    this.table = this.querySelector('[data-vv-size-table]');
    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.setUnit(btn.getAttribute('data-vv-unit')));
    });
    this.setUnit(this.getAttribute('default-unit') || 'in');
  }

  setUnit(unit) {
    this.buttons.forEach((btn) => {
      btn.classList.toggle('is-active', btn.getAttribute('data-vv-unit') === unit);
    });
    if (!this.table) return;
    this.table.querySelectorAll('[data-in]').forEach((cell) => {
      const value = unit === 'cm' ? cell.getAttribute('data-cm') : cell.getAttribute('data-in');
      cell.textContent = value || '';
    });
  }
}
if (!customElements.get('vv-size-toggle')) customElements.define('vv-size-toggle', VVSizeToggle);

document.addEventListener('DOMContentLoaded', vvSyncHeaderHeight);

}
